import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, MapPin, Mail, Phone, X, ShieldCheck } from 'lucide-react';

// WhatsApp SVG Icon component (same style as other icons)
const WhatsAppIcon = ({ className }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Footer = () => {
  const [showVerification, setShowVerification] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [verificationError, setVerificationError] = useState(false);

  const logo = "/logo-white.png";
  const instagramUrl = "https://www.instagram.com/luxemejapan";
  const phoneNumber = "+60172680103";
  const whatsappUrl = `https://wa.me/${phoneNumber.replace(/[^0-9]/g, '')}`;

  // Obfuscated email parts
  const emailParts = ['luxeme', 'japan', '@', 'gmail', '.com'];
  const getEmail = () => emailParts[0] + emailParts[1] + emailParts[2] + emailParts[3] + emailParts[4];

  // Verification challenge
  const challenge = {
    question: "Which of these represents traditional Japanese tea?",
    options: [
      { id: 1, label: "Matcha", image: "🍵", correct: true },
      { id: 2, label: "Coffee", image: "☕", correct: false },
      { id: 3, label: "Wine", image: "🍷", correct: false },
    ]
  };

  const handleEmailClick = () => {
    if (isVerified) {
      window.location.href = `mailto:${getEmail()}`;
    } else {
      setShowVerification(true);
      setSelectedAnswer(null);
      setVerificationError(false);
    }
  };

  const handleVerify = () => {
    if (!selectedAnswer) return;
    
    const selected = challenge.options.find(opt => opt.id === selectedAnswer);
    if (selected && selected.correct) {
      setIsVerified(true);
      setShowVerification(false);
      setTimeout(() => {
        window.location.href = `mailto:${getEmail()}`;
      }, 300);
    } else {
      setVerificationError(true);
      setSelectedAnswer(null);
    }
  };

  return (
    <>
      <footer className="bg-onyx relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold/3 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        
        {/* Main Footer Content */}
        <div className="relative z-10 border-t border-gold/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-16 lg:py-20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
              
              {/* Brand Column */}
              <div className="lg:col-span-1 text-center md:text-left">
                <Link to="/" className="inline-block mb-6">
                  <img
                    src={logo}
                    alt="Luxe Me Japan"
                    className="h-14 sm:h-16 w-auto brightness-110 hover:brightness-125 transition-all duration-500 mx-auto md:mx-0"
                  />
                </Link>
                <p className="text-platinum/50 text-sm leading-relaxed mb-6 max-w-xs mx-auto md:mx-0">
                  Bridging Japanese elegance with global markets through premium beauty and lifestyle curation.
                </p>
                {/* Social Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  {/* Instagram Button */}
                  <a
                    href={instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] px-4 py-2.5 rounded-full text-white text-sm font-medium hover:opacity-90 transition-all duration-300 hover:scale-105 group"
                  >
                    <Instagram className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                    <span>Follow</span>
                  </a>
                  {/* WhatsApp Button */}
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-[#25D366] px-4 py-2.5 rounded-full text-white text-sm font-medium hover:opacity-90 transition-all duration-300 hover:scale-105 group"
                  >
                    <WhatsAppIcon className="w-4 h-4 group-hover:scale-110 transition-transform" />
                    <span>WhatsApp</span>
                  </a>
                </div>
              </div>

              {/* Quick Links */}
              <div className="text-center md:text-left">
                <h4 className="font-serif text-lg text-gold mb-6">Quick Links</h4>
                <nav className="flex flex-col gap-3">
                  <Link to="/" className="text-platinum/50 hover:text-gold transition-colors duration-300 text-sm">
                    Home
                  </Link>
                  <Link to="/about" className="text-platinum/50 hover:text-gold transition-colors duration-300 text-sm">
                    About Us
                  </Link>
                  <Link to="/services" className="text-platinum/50 hover:text-gold transition-colors duration-300 text-sm">
                    Services
                  </Link>
                  <Link to="/brands" className="text-platinum/50 hover:text-gold transition-colors duration-300 text-sm">
                    Our Brands
                  </Link>
                  <Link to="/events" className="text-platinum/50 hover:text-gold transition-colors duration-300 text-sm">
                    Events
                  </Link>
                </nav>
              </div>

              {/* Services - with anchor links */}
              <div className="text-center md:text-left">
                <h4 className="font-serif text-lg text-gold mb-6">Services</h4>
                <nav className="flex flex-col gap-3">
                  <Link to="/services#service-1" className="text-platinum/50 hover:text-gold transition-colors duration-300 text-sm">
                    Market Expansion
                  </Link>
                  <Link to="/services#service-2" className="text-platinum/50 hover:text-gold transition-colors duration-300 text-sm">
                    Brand Storytelling
                  </Link>
                  <Link to="/services#service-3" className="text-platinum/50 hover:text-gold transition-colors duration-300 text-sm">
                    Influencer Collaborations
                  </Link>
                  <Link to="/services#service-4" className="text-platinum/50 hover:text-gold transition-colors duration-300 text-sm">
                    Curation of Excellence
                  </Link>
                </nav>
              </div>

              {/* Contact Info */}
              <div className="text-center md:text-left">
                <h4 className="font-serif text-lg text-gold mb-6">Contact</h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 justify-center md:justify-start">
                    <MapPin className="w-4 h-4 text-gold mt-1 flex-shrink-0" />
                    <p className="text-platinum/50 text-sm">
                      Sunway Visio Tower,<br />
                      Kuala Lumpur, Malaysia
                    </p>
                  </div>
                  <div className="flex items-center gap-3 justify-center md:justify-start">
                    <Phone className="w-4 h-4 text-gold flex-shrink-0" />
                    <a 
                      href={`tel:${phoneNumber}`}
                      className="text-platinum/50 text-sm hover:text-gold transition-colors"
                    >
                      +60 17-268 0103
                    </a>
                  </div>
                  {/* Protected Email */}
                  <button
                    onClick={handleEmailClick}
                    className="flex items-center gap-3 justify-center md:justify-start group w-full"
                  >
                    <Mail className="w-4 h-4 text-gold flex-shrink-0" />
                    <span className="text-platinum/50 text-sm group-hover:text-gold transition-colors flex items-center gap-2">
                      {isVerified ? getEmail() : 'Click to reveal email'}
                      {!isVerified && <ShieldCheck className="w-3 h-3 text-gold/50" />}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/5">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-8">
              <div className="flex justify-center items-center">
                <p className="text-platinum/30 text-[10px] sm:text-xs tracking-widest font-light text-center">
                  © 2026 LUXEMEJAPAN. ALL RIGHTS RESERVED.
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Verification Modal */}
      {showVerification && (
        <div 
          className="fixed inset-0 z-[100] bg-onyx/95 backdrop-blur-xl flex items-center justify-center p-4"
          onClick={() => setShowVerification(false)}
        >
          <div 
            className="bg-ebony rounded-3xl max-w-md w-full border border-gold/20 shadow-2xl shadow-gold/10 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-gold/10 to-transparent p-6 border-b border-gold/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-6 h-6 text-gold" />
                  <h3 className="font-serif text-xl text-platinum">Verification</h3>
                </div>
                <button 
                  onClick={() => setShowVerification(false)}
                  className="text-platinum/50 hover:text-gold transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 sm:p-8">
              <p className="text-platinum/70 text-center mb-6">
                Please verify you are human to access our contact.
              </p>

              {/* Question */}
              <p className="text-gold text-center font-medium mb-6">
                {challenge.question}
              </p>

              {/* Options */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {challenge.options.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => {
                      setSelectedAnswer(option.id);
                      setVerificationError(false);
                    }}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all duration-300 ${
                      selectedAnswer === option.id
                        ? 'border-gold bg-gold/10 scale-105'
                        : 'border-white/10 hover:border-gold/50'
                    }`}
                  >
                    <span className="text-4xl">{option.image}</span>
                    <span className="text-xs text-platinum/70">{option.label}</span>
                  </button>
                ))}
              </div>

              {/* Error Message */}
              {verificationError && (
                <p className="text-red-400 text-sm text-center mb-4">
                  Incorrect answer. Please try again.
                </p>
              )}

              {/* Verify Button */}
              <button
                onClick={handleVerify}
                disabled={!selectedAnswer}
                className={`w-full py-4 rounded-full font-bold tracking-widest uppercase text-xs transition-all duration-500 ${
                  selectedAnswer
                    ? 'bg-gold text-onyx hover:bg-white'
                    : 'bg-platinum/20 text-platinum/40 cursor-not-allowed'
                }`}
              >
                Verify & Reveal Email
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;
